<template>
  <header
    class="w-full h-12 sm:h-14 flex-shrink-0 flex items-center justify-between px-3 sm:px-4 lg:px-5 bg-white dark:bg-gray-900 transition-colors duration-300 border-b border-gray-200 dark:border-gray-800"
  >
    <!-- Left Side -->
    <div class="flex items-center gap-2 sm:gap-3">
      <!-- Mobile Menu Button -->
      <button
        @click="$emit('toggle-mobile-menu')"
        class="text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 lg:hidden focus:outline-none transition-colors p-1"
        aria-label="Toggle sidebar"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          ></path>
        </svg>
      </button>

      <!-- Logo & Page Title -->
      <div class="hidden md:flex items-center gap-2">
        <img src="/logo_alkara.png" alt="Alkara Logo" class="w-6 h-6 sm:w-7 sm:h-7 object-contain">
        <div>
          <h1 class="text-sm sm:text-base font-bold text-gray-800 dark:text-gray-100 truncate">
            {{ pageTitle }}
          </h1>
          <p v-if="pageSubtitle" class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
            {{ pageSubtitle }}
          </p>
        </div>
      </div>
    </div>

    <!-- Right Side -->
    <div class="flex items-center gap-1 sm:gap-2 ml-auto">
      <!-- Language Switcher (Flag) -->
      <button
        @click="toggleLanguage"
        class="text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 p-1.5 sm:p-2 rounded-full transition-colors focus:outline-none"
        :title="locale === 'en' ? 'Switch to ID' : 'Switch to EN'"
      >
        <img
          :src="
            locale === 'en' ? 'https://flagcdn.com/w40/gb.png' : 'https://flagcdn.com/w40/id.png'
          "
          class="w-4 h-4 sm:w-5 sm:h-5 rounded-full object-cover shadow-sm"
          alt="Language"
        />
      </button>



      <!-- User Avatar & Menu -->
      <div class="relative" ref="userMenuRef">
        <button
          @click="toggleUserMenu"
          class="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer hover:ring-2 ring-blue-500 transition-all flex items-center justify-center text-gray-600 dark:text-gray-300"
          aria-label="User menu"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        </button>

        <!-- User Dropdown -->
        <Transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="transform opacity-0 scale-95 -translate-y-2"
          enter-to-class="transform opacity-100 scale-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="transform opacity-100 scale-100 translate-y-0"
          leave-to-class="transform opacity-0 scale-95 -translate-y-2"
        >
          <div
            v-if="showUserMenu"
            class="absolute right-0 mt-3 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden"
          >
            <!-- User Info Header -->
            <div
              class="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700"
            >
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
                    {{ userName }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ userEmail }}</p>
                </div>
              </div>
            </div>

            <!-- Menu Items -->
            <div class="py-2">
              <button
                @click="handleProfileClick"
                class="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-3 transition-colors duration-150"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <span>{{ t('header.profile') || 'Profile' }}</span>
              </button>

              <button
                @click="handleSettingsClick"
                class="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-3 transition-colors duration-150"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>{{ t('header.settings') || 'Settings' }}</span>
              </button>

              <div class="border-t border-gray-100 dark:border-gray-700 my-2"></div>

              <button
                @click="handleLogoutClick"
                class="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 transition-colors duration-150 font-medium"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <span>{{ t('header.logout') }}</span>
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>



    <!-- Logout Confirmation Dialog -->
    <ConfirmDialog
      :is-open="showLogoutConfirm"
      :title="t('auth.logoutConfirmTitle')"
      :message="t('auth.logoutConfirmMessage')"
      :confirm-text="t('auth.logoutConfirmConfirm')"
      :cancel-text="t('auth.logoutConfirmCancel')"
      type="warning"
      @confirm="onLogoutConfirm"
      @cancel="onLogoutCancel"
    />
  </header>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, type Ref, type ComputedRef } from 'vue'
import { useRouter, useRoute, type RouteMeta } from 'vue-router'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import { useAlertStore } from '@/stores/alerts'
import { useAuthStore } from '@/stores/auth'
import { useProfileStore } from '@/stores/profile'
import { onClickOutside, onKeyStroke } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

// Emits
defineEmits(['toggle-mobile-menu'])

// Interface for Custom Route Meta
interface CustomRouteMeta extends RouteMeta {
  title?: string
  subtitle?: string
}

const router = useRouter()
const route = useRoute()
const alertStore = useAlertStore()
const profileStore = useProfileStore()
const showUserMenu: Ref<boolean> = ref(false)
const showLogoutConfirm: Ref<boolean> = ref(false)
const userMenuRef: Ref<HTMLElement | null> = ref(null)

// Initialize i18n
const { locale, t } = useI18n()

// User avatar
const userAvatar = computed(() => profileStore.profile?.avatar)

// Load profile on mount
onMounted(() => {
  profileStore.fetchProfile()
})

// Page title and subtitle from route meta
const pageTitle: ComputedRef<string> = computed(() => {
  const meta = route.meta as CustomRouteMeta
  return meta.title ? t(meta.title) : t('welcome')
})

const pageSubtitle: ComputedRef<string> = computed(() => {
  const meta = route.meta as CustomRouteMeta
  return meta.subtitle ? t(meta.subtitle) : ''
})

// Function to toggle language
function toggleLanguage(): void {
  const newLang = locale.value === 'en' ? 'id' : 'en'
  locale.value = newLang
  localStorage.setItem('preferred-language', newLang)
}

// Toggle user menu
function toggleUserMenu(): void {
  showUserMenu.value = !showUserMenu.value
}



// Close user menu when clicking outside
onClickOutside(userMenuRef, () => {
  showUserMenu.value = false
})

// Close user menu on Escape key
onKeyStroke('Escape', (e: KeyboardEvent) => {
  if (showUserMenu.value) {
    e.preventDefault()
    showUserMenu.value = false
  }
})

// Handle logout
async function handleLogout(): Promise<void> {
  showUserMenu.value = false
  try {
    const auth = useAuthStore()
    await auth.logout()
    alertStore.showAlert(t('header.logout') + ' — OK', 'info')
    router.push({ name: 'login' })
  } catch (err) {
    console.warn('Logout failed:', err)
    alertStore.showError((err as any)?.message || 'Logout failed')
  }
}

// Handle logout button click
function handleLogoutClick(): void {
  showUserMenu.value = false
  showLogoutConfirm.value = true
}

// Cancel logout
function onLogoutCancel(): void {
  showLogoutConfirm.value = false
}

// Confirm logout
async function onLogoutConfirm(): Promise<void> {
  showLogoutConfirm.value = false
  await handleLogout()
}

// Handle profile click
function handleProfileClick(): void {
  showUserMenu.value = false
  router.push({ name: 'profile' })
}

// Handle settings click
function handleSettingsClick(): void {
  showUserMenu.value = false
  router.push({ name: 'settings' })
}

// User information
const userName = computed(() => {
  const auth = useAuthStore()
  return auth.user?.name ?? t('header.demoUser')
})

const userEmail = computed(() => {
  const auth = useAuthStore()
  return auth.user?.email ?? t('header.email')
})
</script>

